import React from 'react';
import type { SelectedVase } from '../../../types';
import type { CanvasSectionBlock } from '../sectionRegistry';
import type { useFlowContext } from '../../flow/FlowProvider';
import ProductGalleryBlockSurface from '../ProductGalleryBlockSurface';
import { GalleryBlockSurface } from '../GalleryBlockSurface';
import { isProductGalleryContent } from '../../../lib/productGallery';
import {
  resolveProductRecordModelPath,
  resolveProductRecordTitle,
  resolveProductSelectionPayload,
  type ProductRecord,
} from '../../../lib/productRecords';
import { resolveProductCardSelectionTarget } from '../../../lib/flowBlockResolution';
import { resolveProductCardSelectActionTargets } from '../../../lib/productCardEventBridge';

type WrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties;
};

type ModelPreviewState = {
  modelPath: string;
  vaseId?: number;
  title?: string;
};

type GalleryBlockRendererProps = {
  block: CanvasSectionBlock;
  blockWrapperProps: WrapperProps;
  selectableBlockHitProps: Record<string, string>;
  selectionTarget: string;
  renderSelectionOverlay: (block: CanvasSectionBlock) => React.ReactNode;
  flow: ReturnType<typeof useFlowContext>;
  sectionId?: string;
  productRecords: ProductRecord[];
  selectedProductIds: Set<number>;
  moveMode: boolean;
  isAdmin: boolean;
  isInteractPreview: boolean;
  onSelectBlock?: (blockId: string | null) => void;
  onOpenBlockContent?: (blockId: string, options?: { galleryItemId?: string }) => void;
  onSelectVase?: (vase: SelectedVase) => void;
  onOpenModelPreview: (preview: ModelPreviewState) => void;
};

export function GalleryBlockRenderer({
  block,
  blockWrapperProps,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
  flow,
  sectionId,
  productRecords,
  selectedProductIds,
  moveMode,
  isAdmin,
  isInteractPreview,
  onSelectBlock,
  onOpenBlockContent,
  onSelectVase,
  onOpenModelPreview,
}: GalleryBlockRendererProps) {
  const content =
    block.content && typeof block.content === 'object'
      ? (block.content as Record<string, unknown>)
      : undefined;
  if (isProductGalleryContent(content)) {
    return (
      <div key={block.id} {...blockWrapperProps}>
        <div {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})} className="h-full w-full">
          <ProductGalleryBlockSurface
            content={content}
            style={
              block.style && typeof block.style === 'object'
                ? (block.style as Record<string, unknown>)
                : undefined
            }
            products={productRecords}
            selectedProductIds={selectedProductIds}
            moveMode={moveMode}
            selectionOverlay={renderSelectionOverlay(block)}
            onSelectProduct={(product) => {
              if (isAdmin && !isInteractPreview) {
                onSelectBlock?.(block.id);
                return;
              }
              const selectedVase = resolveProductSelectionPayload(product);
              if (!selectedVase) return;
              const target = resolveProductCardSelectionTarget(flow.flows, {
                block,
                sectionId,
                vase: selectedVase,
              });
              if (target) {
                flow.patchFlowValues(target.flowId, target.values);
              }
              const actionTargets = resolveProductCardSelectActionTargets(flow.flows, {
                blockId: block.id,
                blockType: 'gallery',
                sectionId,
              });
              if (actionTargets?.selectFieldId) {
                flow.patchFlowValues(actionTargets.flowId, {
                  [actionTargets.selectFieldId]: Date.now(),
                });
              }
              actionTargets?.targetNodeIds.forEach((nodeId) => {
                flow.invokeFlowNode(actionTargets.flowId, nodeId);
              });
              onSelectVase?.(selectedVase);
            }}
            onViewModel={(product) => {
              if (isAdmin && !isInteractPreview) {
                onSelectBlock?.(block.id);
                return;
              }
              const modelPath = resolveProductRecordModelPath(product).trim();
              if (!modelPath) return;
              onOpenModelPreview({
                modelPath,
                vaseId:
                  Number.isFinite(product.modelId) && Number(product.modelId) > 0
                    ? Number(product.modelId)
                    : product.id,
                title: resolveProductRecordTitle(product) || product.name,
              });
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div key={block.id} {...blockWrapperProps}>
      <div {...(selectionTarget !== 'content' ? selectableBlockHitProps : {})} className="h-full w-full">
        <GalleryBlockSurface
          content={content}
          style={
            block.style && typeof block.style === 'object'
              ? (block.style as Record<string, unknown>)
              : undefined
          }
          moveMode={moveMode}
          isInteractPreview={isInteractPreview}
          enableEditorSlots={isAdmin}
          onOpenContent={(galleryItemId) => {
            onSelectBlock?.(block.id);
            onOpenBlockContent?.(block.id, { galleryItemId });
          }}
          selectionOverlay={renderSelectionOverlay(block)}
        />
      </div>
    </div>
  );
}
